// React
import React, { useState } from "react";
import PropTypes from "prop-types";

// Internal Components
import Column from "./components/Lists/Column";

// External Components
import { DragDropContext } from "react-beautiful-dnd";
import { StrictModeDroppable } from "./hooks/Dropable";

/**
 * Wrapper: Handles the specific container type and Docking classes
 * Framer Motion has been completely removed to prevent DnD event hijacking.
 */
const BoardWrapper = ({
  day,
  viewType,
  children,
  handleColumnChange,
  dockClass,
}) => {
  // 1. Desktop Day View: Docked Static Container
  if (day && viewType === "desktop") {
    return (
      <div className={`board-dock-container ${dockClass}`}>{children}</div>
    );
  }

  // 2. Desktop Week View: Standard Container
  // (React Beautiful DnD natively handles the left/right column dragging inside this)
  if (!day && viewType === "desktop") {
    return <div id="wektest">{children}</div>;
  }

  // 3. Mobile Views
  const mobileProps =
    viewType !== "desktop"
      ? {
          "data-name": "background",
          onClick: handleColumnChange,
        }
      : {};

  return (
    <div id={day ? "wektestmobile" : "wektest"} {...mobileProps}>
      {children}
    </div>
  );
};

const Board = (props) => {
  const {
    day,
    isCombineEnabled,
    ordered,
    onDragEnd,
    initial,
    overlay,
    containerHeight,
    viewType,
    viewWidth, // Kept in props in case you need it elsewhere, but no longer needed for Framer constraints
    handleColumnChange,
    ...passThroughProps
  } = props;

  // --- DOCKING LOGIC ---
  const DOCK_POSITIONS = [
    "dock-top-right",
    "dock-bottom-right",
    "dock-top-left",
  ];
  const [positionIndex, setPositionIndex] = useState(0);

  const cyclePosition = () => {
    setPositionIndex((prev) => (prev + 1) % DOCK_POSITIONS.length);
  };
  // ---------------------

  const droppableId = day ? "dead" : "board";
  const columnTypeString = `${day ? "day" : "week"}--${viewType}`;
  const boardClass =
    day && viewType === "desktop" ? "board--scale--day" : "board--scale";

  return (
    <BoardWrapper
      day={day}
      viewType={viewType}
      handleColumnChange={handleColumnChange}
      dockClass={DOCK_POSITIONS[positionIndex]}
    >
      <DragDropContext onDragEnd={onDragEnd}>
        <StrictModeDroppable
          droppableId={droppableId}
          type="COLUMN"
          direction="horizontal" // <--- This is what allows columns to be dragged left/right!
          ignoreContainerClipping={Boolean(containerHeight)}
          isCombineEnabled={isCombineEnabled}
        >
          {(provided) => (
            <div
              className={boardClass}
              ref={provided.innerRef}
              {...provided.droppableProps}
              style={{ height: "100%", display: "flex" }} // Ensure display flex so horizontal DnD works
            >
              {ordered &&
                ordered.map((key, index) => (
                  <Column
                    key={key}
                    index={index}
                    title={key}
                    order={ordered}
                    objective={initial[key]}
                    overlay={overlay}
                    columnType={columnTypeString}
                    isCombineEnabled={isCombineEnabled}
                    viewType={viewType}
                    day={day}
                    cyclePosition={cyclePosition}
                    {...passThroughProps}
                  />
                ))}
              {provided.placeholder}
            </div>
          )}
        </StrictModeDroppable>
      </DragDropContext>
    </BoardWrapper>
  );
};

Board.defaultProps = {
  isCombineEnabled: false,
};

Board.propTypes = {
  isCombineEnabled: PropTypes.bool,
};

export default Board;
